import { theme } from '@traba/theme'
import styled from 'styled-components'
import {
  AGENT_ACTIVITY,
  CARRIER_PERFORMANCE,
  OPS_FACILITY_TODAY,
  OPS_TODAY,
  type AgentActivityRow,
  type CarrierPerfRow,
  type OpsFacilityToday,
} from './data'
import {
  DashboardHeader,
  DiscussWithNeoButton,
  HeroCard,
  HeroVerdict,
  InlineBar,
  InsightCallout,
  MetricCard,
  MetricRow,
  SectionCard,
  SectionHeader,
  SectionSubtitle,
  SectionTitle,
  ShellRoot,
  TwoColRow,
} from './DashboardShell'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCount(value: number): string {
  return value.toLocaleString('en-US')
}

// ---------------------------------------------------------------------------
// Facility tiles (hero centerpiece — bigger, prettier)
// ---------------------------------------------------------------------------

const FacilityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: ${theme.space.xs}px;
`

const FacilityTile = styled.div<{ $tone: 'good' | 'warning' | 'critical' }>`
  border: 1px solid ${theme.colors.Grey20};
  border-radius: ${theme.border.radius};
  padding: ${theme.space.sm}px ${theme.space.med}px;
  background: ${({ $tone }) => {
    if ($tone === 'critical') {
      return `linear-gradient(135deg, ${theme.colors.Red10} 0%, ${theme.colors.White} 80%)`
    }
    if ($tone === 'warning') {
      return `linear-gradient(135deg, ${theme.colors.Yellow10} 0%, ${theme.colors.White} 80%)`
    }
    return theme.colors.White
  }};
  display: flex;
  flex-direction: column;
  gap: ${theme.space.xs}px;
  position: relative;
  overflow: hidden;
  box-shadow: ${theme.shadow.sm};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: 4px;
    background: ${({ $tone }) => {
      if ($tone === 'critical') {
        return theme.colors.Red60
      }
      if ($tone === 'warning') {
        return theme.colors.Yellow70
      }
      return theme.colors.Green70
    }};
  }
`

const FacilityTileTitle = styled.div`
  font-family: ${theme.typography.fontFamily};
  font-size: 13px;
  font-weight: 600;
  color: ${theme.colors.MidnightBlue};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`

const FacilityTileHero = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${theme.space.xxs}px;
`

const FacilityTileOrders = styled.div`
  font-family: ${theme.typography.fontFamily};
  font-size: 32px;
  font-weight: 700;
  color: ${theme.colors.MidnightBlue};
  letter-spacing: -0.02em;
  line-height: 1;
`

const FacilityTilePace = styled.div<{ $abovePace: boolean }>`
  font-family: ${theme.typography.fontFamily};
  font-size: 12px;
  font-weight: 600;
  color: ${({ $abovePace }) =>
    $abovePace ? theme.colors.Green80 : theme.colors.Grey60};
`

const FacilityTileForecast = styled.div`
  font-family: ${theme.typography.fontFamily};
  font-size: 12px;
  color: ${theme.colors.Grey60};
`

const FacilityTileFooter = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.space.xxs}px;
  margin-top: ${theme.space.xxs}px;
  padding-top: ${theme.space.xxs}px;
  border-top: 1px solid ${theme.colors.Grey20};
`

const FacilityFooterItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const FacilityFooterLabel = styled.div`
  font-family: ${theme.typography.fontFamily};
  font-size: 10px;
  color: ${theme.colors.Grey60};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`

const FacilityFooterValue = styled.div<{
  $tone?: 'good' | 'warning' | 'critical'
}>`
  font-family: ${theme.typography.fontFamily};
  font-size: 15px;
  font-weight: 600;
  color: ${({ $tone }) => {
    if ($tone === 'critical') {
      return theme.colors.Red80
    }
    if ($tone === 'warning') {
      return theme.colors.Yellow80
    }
    return theme.colors.MidnightBlue
  }};
`

// ---------------------------------------------------------------------------
// Agents list
// ---------------------------------------------------------------------------

const AgentList = styled.div`
  display: flex;
  flex-direction: column;
`

const AgentListHeader = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 0.8fr 0.6fr 1.8fr;
  gap: ${theme.space.sm}px;
  padding: 0 ${theme.space.xs}px ${theme.space.xxs}px;
  font-family: ${theme.typography.fontFamily};
  font-size: 11px;
  font-weight: 600;
  color: ${theme.colors.Grey60};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid ${theme.colors.Grey20};
  margin-bottom: ${theme.space.xs}px;
`

const AgentRowEl = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 0.8fr 0.6fr 1.8fr;
  align-items: center;
  gap: ${theme.space.sm}px;
  padding: ${theme.space.xxs}px ${theme.space.xs}px;
  border-radius: 8px;

  & + & {
    margin-top: 2px;
  }
`

const AgentName = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.space.xxs}px;
  font-family: ${theme.typography.fontFamily};
  font-size: 14px;
  font-weight: 500;
  color: ${theme.colors.MidnightBlue};
`

const AgentFacilityPill = styled.span`
  font-family: ${theme.typography.fontFamily};
  font-size: 11px;
  color: ${theme.colors.Violet80};
  background: ${theme.colors.Violet10};
  border: 1px solid ${theme.colors.Violet20};
  padding: 2px 8px;
  border-radius: 999px;
  white-space: nowrap;
`

const AgentRuns = styled.div`
  font-family: ${theme.typography.fontFamily};
  font-size: 13px;
  font-weight: 600;
  color: ${theme.colors.MidnightBlue};
`

const AgentOutput = styled.div<{ $warn: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.space.xxs}px;
  font-family: ${theme.typography.fontFamily};
  font-size: 13px;
  color: ${({ $warn }) =>
    $warn ? theme.colors.Yellow80 : theme.colors.Grey80};
`

const AgentStatusDot = styled.span<{ $tone: 'good' | 'warning' }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $tone }) =>
    $tone === 'warning' ? theme.colors.Yellow70 : theme.colors.Green70};
  flex-shrink: 0;
`

// ---------------------------------------------------------------------------
// Exceptions list
// ---------------------------------------------------------------------------

const ExceptionsList = styled.div`
  display: flex;
  flex-direction: column;
`

const ExceptionRow = styled.div<{ $tone: 'critical' | 'warning' }>`
  display: grid;
  grid-template-columns: 1.6fr 1fr 0.6fr;
  align-items: center;
  gap: ${theme.space.sm}px;
  padding: ${theme.space.xs}px ${theme.space.xs}px;
  border-radius: 8px;
  position: relative;

  & + & {
    margin-top: 2px;
  }

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 8px;
    bottom: 8px;
    width: 3px;
    border-radius: 999px;
    background: ${({ $tone }) =>
      $tone === 'critical' ? theme.colors.Red60 : theme.colors.Yellow70};
  }
`

const ExceptionPrimary = styled.div<{ $tone: 'critical' | 'warning' }>`
  font-family: ${theme.typography.fontFamily};
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.MidnightBlue};
  padding-left: 12px;

  small {
    display: block;
    font-size: 12px;
    font-weight: 400;
    color: ${({ $tone }) =>
      $tone === 'critical' ? theme.colors.Red80 : theme.colors.Yellow80};
  }
`

const ExceptionSecondary = styled.div`
  font-family: ${theme.typography.fontFamily};
  font-size: 13px;
  color: ${theme.colors.Grey80};
`

const ExceptionLate = styled.div<{ $tone: 'critical' | 'warning' }>`
  font-family: ${theme.typography.fontFamily};
  font-size: 14px;
  font-weight: 700;
  color: ${({ $tone }) =>
    $tone === 'critical' ? theme.colors.Red80 : theme.colors.Yellow80};
  text-align: right;
`

// ---------------------------------------------------------------------------
// Carrier perf
// ---------------------------------------------------------------------------

const CarrierList = styled.div`
  display: flex;
  flex-direction: column;
`

const CarrierListHeader = styled.div`
  display: grid;
  grid-template-columns: 1.3fr 1.2fr 1.2fr 1.2fr;
  gap: ${theme.space.sm}px;
  padding: 0 ${theme.space.xs}px ${theme.space.xxs}px;
  font-family: ${theme.typography.fontFamily};
  font-size: 11px;
  font-weight: 600;
  color: ${theme.colors.Grey60};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid ${theme.colors.Grey20};
  margin-bottom: ${theme.space.xs}px;
`

const CarrierRowEl = styled.div`
  display: grid;
  grid-template-columns: 1.3fr 1.2fr 1.2fr 1.2fr;
  align-items: center;
  gap: ${theme.space.sm}px;
  padding: ${theme.space.xs}px ${theme.space.xs}px;
  border-radius: 8px;

  & + & {
    margin-top: 2px;
  }
`

const CarrierName = styled.div`
  font-family: ${theme.typography.fontFamily};
  font-size: 14px;
  font-weight: 700;
  color: ${theme.colors.MidnightBlue};
  line-height: 1.25;
`

const CarrierSub = styled.div`
  font-family: ${theme.typography.fontFamily};
  font-size: 12px;
  color: ${theme.colors.Grey60};
  font-weight: 400;
  margin-top: 2px;
`

const CarrierIdentity = styled.div`
  display: flex;
  flex-direction: column;
`

// ---------------------------------------------------------------------------
// Exception sample (top 4 of 22)
// ---------------------------------------------------------------------------

const CRITICAL_EXCEPTIONS_SAMPLE: Array<{
  id: string
  brand: string
  customer: string
  issue: string
  carrier: string
  late: string
  tone: 'critical' | 'warning'
}> = [
  {
    id: 'exc-1',
    brand: 'Northwind Apparel',
    customer: 'Riley K.',
    issue: 'Stuck in Cleveland',
    carrier: 'UPS',
    late: '4d',
    tone: 'critical',
  },
  {
    id: 'exc-2',
    brand: 'Trailmix Provisions',
    customer: 'Marcus T.',
    issue: 'Lost · no scan 7d',
    carrier: 'USPS',
    late: '7d',
    tone: 'critical',
  },
  {
    id: 'exc-3',
    brand: 'OakHill Skincare',
    customer: 'Dana M.',
    issue: 'Delivered damaged',
    carrier: 'UPS',
    late: '—',
    tone: 'warning',
  },
  {
    id: 'exc-4',
    brand: 'Briar Coffee Roasters',
    customer: 'Emma R.',
    issue: 'Stuck in Memphis',
    carrier: 'USPS',
    late: '3d',
    tone: 'warning',
  },
]

// ---------------------------------------------------------------------------
// Tile + row renderers
// ---------------------------------------------------------------------------

function FacilityTileBlock({ f }: { f: OpsFacilityToday }) {
  // Thresholds calibrated to the network's actual exception range
  // (3 - 8 critical per facility today). Charlotte at 8 → critical.
  const exceptionsToneItem: 'good' | 'warning' | 'critical' =
    f.exceptionsCritical >= 8
      ? 'critical'
      : f.exceptionsCritical >= 5
        ? 'warning'
        : 'good'
  return (
    <FacilityTile $tone={f.status}>
      <FacilityTileTitle>{f.name}</FacilityTileTitle>
      <FacilityTileHero>
        <FacilityTileOrders>{formatCount(f.ordersToday)}</FacilityTileOrders>
        <FacilityTilePace $abovePace={f.pacePct >= 100}>
          {f.pacePct}% pace
        </FacilityTilePace>
      </FacilityTileHero>
      <FacilityTileForecast>
        forecast {formatCount(f.ordersForecast)} today
      </FacilityTileForecast>
      <FacilityTileFooter>
        <FacilityFooterItem>
          <FacilityFooterLabel>Critical exc.</FacilityFooterLabel>
          <FacilityFooterValue $tone={exceptionsToneItem}>
            {f.exceptionsCritical}
          </FacilityFooterValue>
        </FacilityFooterItem>
        <FacilityFooterItem>
          <FacilityFooterLabel>FT utilization</FacilityFooterLabel>
          <FacilityFooterValue>{f.ftUtilizationPct}%</FacilityFooterValue>
        </FacilityFooterItem>
      </FacilityTileFooter>
    </FacilityTile>
  )
}

function AgentRow({ a }: { a: AgentActivityRow }) {
  return (
    <AgentRowEl>
      <AgentName>
        <AgentStatusDot $tone={a.status} />
        {a.name}
      </AgentName>
      <div>
        <AgentFacilityPill>{a.facility}</AgentFacilityPill>
      </div>
      <AgentRuns>{a.runs}</AgentRuns>
      <AgentOutput $warn={a.status === 'warning'}>{a.output}</AgentOutput>
    </AgentRowEl>
  )
}

function CarrierRow({ c, maxRate }: { c: CarrierPerfRow; maxRate: number }) {
  return (
    <CarrierRowEl>
      <CarrierIdentity>
        <CarrierName>{c.carrier}</CarrierName>
        <CarrierSub>
          {c.claimsThisWeek} {c.claimsThisWeek === 1 ? 'claim' : 'claims'} · $
          {c.dollarsInMotion.toLocaleString('en-US')}
        </CarrierSub>
      </CarrierIdentity>
      <InlineBar
        widthPct={(c.lossPct / maxRate) * 100}
        label={`${c.lossPct.toFixed(2)}%`}
        tone={c.lossPct >= 0.25 ? 'warning' : 'neutral'}
      />
      <InlineBar
        widthPct={(c.damagePct / 0.1) * 100}
        label={`${c.damagePct.toFixed(2)}%`}
        tone={c.damagePct >= 0.06 ? 'warning' : 'neutral'}
      />
      <InlineBar
        widthPct={(c.latePct / 1.5) * 100}
        label={`${c.latePct.toFixed(1)}%`}
        tone={c.latePct >= 1 ? 'warning' : 'neutral'}
      />
    </CarrierRowEl>
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OpsPulseDashboard() {
  const onTimeDelta =
    OPS_TODAY.onTimeShipPctThisWeek - OPS_TODAY.onTimeShipPctLastWeek
  const maxLossRate = Math.max(...CARRIER_PERFORMANCE.map((c) => c.lossPct))

  return (
    <ShellRoot>
      <DashboardHeader
        title="Operations Pulse"
        subtitle="Real-time + this-week network operations. Today's volume, on-time performance, exceptions, agent activity, and carrier health across all 4 fulfillment centers."
        rightSlot={
          <DiscussWithNeoButton
            scriptId="post-shipment-support"
            prompt="Show me the post-shipment monitor"
          />
        }
      />

      <HeroVerdict
        tone="neutral"
        eyebrow="Morning roll-up · today"
        headline="8 agents healthy · 5,350 labels printed by 10:02am · 22 critical exceptions in queue"
        sub="Network on pace at 98%. Charlotte is the outlier — 85% pace and 8 critical exceptions queued, the most in the network. Indy + Atlanta + Reno tracking healthy. 0 failed agent runs in the last 30 days."
      />

      <MetricRow>
        <MetricCard
          tone="neutral"
          label="Today's orders"
          value={`${formatCount(OPS_TODAY.ordersSoFar)}`}
          sub={`forecast ${formatCount(OPS_TODAY.ordersForecast)} · ${OPS_TODAY.pacePct}% pace`}
        />
        <MetricCard
          tone={onTimeDelta >= 0 ? 'good' : 'warning'}
          label="On-time ship · this week"
          value={`${OPS_TODAY.onTimeShipPctThisWeek}%`}
          sub={`last week ${OPS_TODAY.onTimeShipPctLastWeek}% (${onTimeDelta >= 0 ? '+' : ''}${onTimeDelta.toFixed(1)} pts)`}
        />
        <MetricCard
          tone="warning"
          label="Active exceptions"
          value={`${OPS_TODAY.activeExceptionsCritical}`}
          sub={`critical · ${OPS_TODAY.activeExceptionsTotal} total in-flight`}
        />
        <MetricCard
          tone="good"
          label="Agents healthy"
          value={`${OPS_TODAY.agentsActive}/${OPS_TODAY.agentsTotal}`}
          sub={`${OPS_TODAY.failedRunsLast30Days} failed runs · 30 days`}
        />
      </MetricRow>

      <InsightCallout
        tone="critical"
        glyph="●"
        text={
          <>
            <strong>Charlotte at 85% pace · 8 critical exceptions</strong> —
            below forecast and tracking the network's worst exception load.
          </>
        }
      />

      <HeroCard>
        <SectionTitle>Facilities · today</SectionTitle>
        <SectionSubtitle>
          Today's volume, pace, exception load, and FT utilization per
          fulfillment center.
        </SectionSubtitle>
        <FacilityGrid>
          {OPS_FACILITY_TODAY.map((f) => (
            <FacilityTileBlock key={f.id} f={f} />
          ))}
        </FacilityGrid>
      </HeroCard>

      <TwoColRow>
        <SectionCard>
          <SectionHeader
            title="Critical exceptions · last 48h"
            subtitle={`Top 4 of ${OPS_TODAY.activeExceptionsCritical} critical exceptions, by severity. Riley K. at the top — Northwind UPS Cleveland 4d.`}
            action={
              <DiscussWithNeoButton
                compact
                scriptId="post-shipment-support"
                prompt="Show me the post-shipment monitor"
                label="Discuss exceptions"
              />
            }
          />
          <ExceptionsList>
            {CRITICAL_EXCEPTIONS_SAMPLE.map((e) => (
              <ExceptionRow key={e.id} $tone={e.tone}>
                <ExceptionPrimary $tone={e.tone}>
                  {e.brand} · {e.customer}
                  <small>{e.issue}</small>
                </ExceptionPrimary>
                <ExceptionSecondary>{e.carrier}</ExceptionSecondary>
                <ExceptionLate $tone={e.tone}>{e.late}</ExceptionLate>
              </ExceptionRow>
            ))}
          </ExceptionsList>
        </SectionCard>

        <SectionCard>
          <SectionHeader
            title="Carrier performance · this week"
            subtitle="Loss / damage / late rates by carrier. Bar visualizes rate relative to network-wide max."
            action={
              <DiscussWithNeoButton
                compact
                scriptId="carrier-claims-agent"
                prompt="Show me the carrier claims queue"
                label="Discuss claims"
              />
            }
          />
          <CarrierListHeader>
            <div>Carrier</div>
            <div>Loss</div>
            <div>Damage</div>
            <div>Late</div>
          </CarrierListHeader>
          <CarrierList>
            {CARRIER_PERFORMANCE.map((c) => (
              <CarrierRow key={c.carrier} c={c} maxRate={maxLossRate} />
            ))}
          </CarrierList>
        </SectionCard>
      </TwoColRow>

      <SectionCard>
        <SectionHeader
          title="This week · agent activity"
          subtitle="7-day rollup across all 8 agents. 0 failed runs in 30 days."
          action={
            <DiscussWithNeoButton
              compact
              scriptId="bulk-label-agent"
              prompt="Walk me through the agents"
              label="Discuss agents"
            />
          }
        />
        <AgentListHeader>
          <div>Agent</div>
          <div>Scope</div>
          <div>Runs</div>
          <div>Output</div>
        </AgentListHeader>
        <AgentList>
          {AGENT_ACTIVITY.map((a) => (
            <AgentRow key={a.id} a={a} />
          ))}
        </AgentList>
      </SectionCard>
    </ShellRoot>
  )
}
